import { ChangeEvent, useRef } from 'react';

import { Button, Input, dialogService } from '@thefarmersfront/kitchen';

import { InfoText } from '~/components/common';

import { CLAIM_IMAGE, CLAIM_NOTICE } from '~/constants';

import styles from './ReceiveReturnDisposalClaimModal.module.scss';

type TypeClaimImageUploaderProps = {
  files: File[];
  onChange: (next: File[]) => void;
  disabled?: boolean;
};

/**
 * [입고관리 > 회송/폐기 관리] 클레임 신청 모달 - 거래명세서 이미지 업로더
 *
 * @note 파일명 Input(읽기전용) + [파일 찾기] 버튼 구조. 파일은 신청 시점에 함께 전송한다.
 *       선택 시 형식(JPG/PNG)·개별/총 용량·개수 제약을 검증한다.
 */
const ClaimImageUploader = ({ files, onChange, disabled }: TypeClaimImageUploaderProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSelectFiles = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files ?? []);
    // 동일 파일 재선택을 허용하기 위해 input 값을 비운다.
    e.target.value = '';
    if (selectedFiles.length === 0) return;

    if (selectedFiles.some((file) => !CLAIM_IMAGE.ALLOWED_TYPES.some((type) => type === file.type))) {
      dialogService.addDialog({ children: CLAIM_NOTICE.ERROR_TYPE });
      return;
    }
    if (selectedFiles.some((file) => file.size > CLAIM_IMAGE.MAX_FILE_SIZE)) {
      dialogService.addDialog({ children: CLAIM_NOTICE.ERROR_FILE_SIZE });
      return;
    }
    if (selectedFiles.length > CLAIM_IMAGE.MAX_COUNT) {
      dialogService.addDialog({ children: CLAIM_NOTICE.ERROR_MAX_COUNT });
      return;
    }
    if (selectedFiles.reduce((sum, file) => sum + file.size, 0) > CLAIM_IMAGE.MAX_TOTAL_SIZE) {
      dialogService.addDialog({ children: CLAIM_NOTICE.ERROR_TOTAL_SIZE });
      return;
    }

    onChange(selectedFiles);
  };

  return (
    <div className={styles['uploader']}>
      <div className={styles['file-input-row']}>
        <Input
          className={styles['file-name']}
          type="text"
          size="small"
          readOnly
          value={files.map((file) => file.name).join(', ')}
        />
        <Button
          size="small"
          variant="contained"
          color="secondary"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          파일 찾기
        </Button>
      </div>
      <div className={styles['uploader-guide']}>
        <InfoText size="small">{CLAIM_NOTICE.IMAGE_GUIDE_SIGNATURE}</InfoText>
        <InfoText size="small">{CLAIM_NOTICE.IMAGE_GUIDE_CONSTRAINT}</InfoText>
      </div>
      <input
        ref={inputRef}
        type="file"
        className={styles['file-input']}
        accept={CLAIM_IMAGE.ACCEPT}
        multiple
        onChange={handleSelectFiles}
      />
    </div>
  );
};

export default ClaimImageUploader;
